import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscribeSessionExpired } from '../client';
import { STALE_MS } from '../config';
import { getAuthMe } from '../endpoints';
import { isApiError } from '../errors';
import { queryKeys } from '../queryKeys';
import type { AuthIdentity } from '../types';

export type AccessState = 'checking' | 'signed_in' | 'signed_out' | 'unavailable';

export interface AuthMeResult {
  readonly identity: AuthIdentity | null;
  readonly access: AccessState;
  readonly error: unknown;
  readonly refetch: () => void;
}

// What the boot access check earns the reader. Only a 401 is a final "you are
// not signed in": a 429 or a 5xx is a load artefact on this backend, and after
// the client's own backoff has given up on it, a reader who was already inside
// keeps the shell with the failure printed on it. Losing the whole terminal to
// one unlucky response was the P0 measured in docs/evidence/final.
export type AccessFrame = 'checking' | 'shell' | 'shell_degraded' | 'door';

export function accessFrame(access: AccessState, hadSession: boolean): AccessFrame {
  if (access === 'checking') return 'checking';
  if (access === 'signed_in') return 'shell';
  if (access === 'unavailable' && hadSession) return 'shell_degraded';
  return 'door';
}

// The access check on boot. Retries live in the client (10 s abort, then 1, 4
// and 10 s, longer on Retry-After), so TanStack itself does not retry. A 401
// means signed out; any other failure is "unavailable" with the reason kept.
export function useAuthMe(): AuthMeResult {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.authMe(),
    queryFn: ({ signal }) => getAuthMe(signal),
    retry: false,
    staleTime: STALE_MS.authMe,
    refetchOnWindowFocus: true,
  });

  useEffect(() => subscribeSessionExpired(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.authMe() });
  }), [queryClient]);

  const signedOut = isApiError(query.error) && query.error.status === 401;
  const access: AccessState = query.isPending
    ? 'checking'
    : query.data
      ? 'signed_in'
      : signedOut
        ? 'signed_out'
        : 'unavailable';

  return {
    identity: query.data ?? null,
    access,
    error: signedOut ? null : query.error,
    refetch: () => void query.refetch(),
  };
}
